import { Link, useParams } from "react-router-dom";
import { guides } from "@/mocks/guides";
import { siteConfig } from "@/mocks/site";
import GuideCard from "@/components/feature/GuideCard";

function scrollToSection(i: number) {
  const el = document.getElementById(`sec-${i}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function ZhinanDetailPage() {
  const { id } = useParams();
  const guide = guides.find((g) => g.id === id);

  if (!guide) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-24 text-center">
        <i className="ri-book-line text-6xl text-foreground-300" />
        <h1 className="mt-6 font-display text-3xl font-black text-foreground-950">这篇指南走丢了</h1>
        <Link to="/#zhinan" className="mt-6 inline-flex whitespace-nowrap items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-accent-600 cursor-pointer">
          回到吃瓜指南
        </Link>
      </section>
    );
  }

  const related = guide.relatedGuides.map((gid) => guides.find((g) => g.id === gid)!).filter(Boolean);

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: guide.title,
          alternativeHeadline: guide.subtitle,
          description: guide.excerpt,
          image: [guide.cover],
          articleSection: guide.catName,
          datePublished: "2026-09-01",
          dateModified: "2026-09-06",
          timeRequired: guide.readTime,
          author: { "@type": "Organization", name: siteConfig.name },
          publisher: { "@type": "Organization", name: siteConfig.name, url: siteConfig.siteUrl },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${siteConfig.siteUrl}/zhinan/${guide.id}` },
          inLanguage: "zh-CN",
        })}
      </script>

      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="mx-auto max-w-3xl text-center">
            <div className="flex items-center justify-center gap-2 text-xs">
              <Link to="/#zhinan" className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-800 cursor-pointer">
                <i className="ri-arrow-left-s-line" />
                吃瓜指南
              </Link>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-500">{guide.catName}</span>
            </div>
            <h1 className="mt-4 font-display text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {guide.title}
            </h1>
            <p className="mt-3 text-base font-medium text-accent-700 md:text-lg">{guide.subtitle}</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1"><i className="ri-time-line" />阅读 {guide.readTime}</span>
              <span className="inline-flex items-center gap-1"><i className="ri-calendar-line" />更新于 {guide.updated}</span>
              <span className="inline-flex items-center gap-1"><i className="ri-building-line" />{siteConfig.name}</span>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-1.5">
              {guide.tags.map((t) => (
                <span key={t} className="rounded-md bg-background-50 px-2.5 py-1 text-xs text-secondary-900 ring-1 ring-background-200">
                  #{t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 md:px-6 py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* TOC */}
          <aside className="hidden lg:col-span-3 lg:block">
            <nav className="sticky top-28 space-y-1" aria-label="目录">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-foreground-400">本页目录</p>
              {guide.sections.map((s, i) => (
                <button
                  key={s.heading}
                  type="button"
                  onClick={() => scrollToSection(i)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-700 cursor-pointer"
                >
                  <span className="font-display text-primary-500">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </button>
              ))}
              <div className="mt-6 rounded-xl bg-primary-50 p-4">
                <p className="text-sm font-semibold text-primary-900">看完还有疑问？</p>
                <Link to="/faq" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800 cursor-pointer">
                  去常见问题看看
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </nav>
          </aside>

          {/* content */}
          <article className="lg:col-span-9">
            <figure className="overflow-hidden rounded-2xl border border-background-200">
              <img src={guide.cover} alt={guide.title} className="aspect-[21/9] w-full object-cover" />
            </figure>

            <div className="mx-auto mt-8 max-w-2xl">
              {guide.sections.map((s, i) => (
                <section key={s.heading} id={`sec-${i}`} className="mb-9 scroll-mt-28">
                  <h2 className="font-display text-xl font-black text-foreground-950 md:text-2xl">
                    <span className="mr-2 text-accent-600">{String(i + 1).padStart(2, "0")}</span>
                    {s.heading}
                  </h2>
                  {s.body.map((p, pi) => (
                    <p key={pi} className="mt-4 text-[15px] leading-[1.95] text-foreground-700">{p}</p>
                  ))}
                </section>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-2xl bg-accent-700 p-6 text-background-50 sm:flex-row md:p-7">
              <div>
                <h2 className="font-display text-lg font-bold md:text-xl">把这篇方法用起来，吃瓜立刻变明白</h2>
                <p className="mt-1 text-sm text-accent-100">结合一篇真实瓜单练手，效果更好。</p>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                <Link to="/gua/e2" className="inline-flex whitespace-nowrap items-center justify-center gap-1.5 rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-accent-900 hover:bg-background-100 cursor-pointer">
                  去看看最新瓜单
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/#gua" className="inline-flex whitespace-nowrap items-center justify-center gap-1.5 rounded-md border border-accent-300 px-5 py-2.5 text-sm font-semibold text-accent-50 hover:bg-accent-800 cursor-pointer">
                  本期全部瓜
                </Link>
              </div>
            </div>
          </article>
        </div>

        {/* related guides */}
        {related.length > 0 && (
          <section className="mt-12 border-t border-background-200 pt-10">
            <h2 className="font-display text-2xl font-black text-foreground-950">继续补课</h2>
            <div className="mt-6 grid grid-cols-1 gap-4">
              {related.map((g) => (
                <GuideCard key={g.id} guide={g} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}